import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import CultivarCard from '../../components/cultivar/CultivarCard';
import CultivarFilters, { type CultivarFilterValues } from '../../components/cultivar/CultivarFilters';
import PbrNotice from '../../components/cultivar/PbrNotice';
import { isPbrProtected } from '../../constants/pbr';
import { Leaf, Plus, ChevronLeft, ChevronRight, Loader2, Settings, ClipboardCheck, Sprout, BarChart3, History, ClipboardPen } from 'lucide-react';
import { isLegacyCultivarImportOpen } from '../../services/legacyCultivarImport';
import type { Cultivar, Pagination } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';
import { useAuth } from '../../context/AuthContext';
import { useModuleLicense } from '../../hooks/useModuleLicense';

function CultivarRegistry() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  // pbr/admin licence holders may edit own-org cultivars.
  const { hasAccess: canWrite } = useModuleLicense('cultivar', ['pbr', 'admin']);
  // admin licence unlocks the Cultivar Admin area.
  const { hasAccess: isCultivarAdmin } = useModuleLicense('cultivar', 'admin');
  // A Sales seat alone browses the registry to reach a cultivar's sales notes;
  // the cultivar tools behind these buttons need a Cultivar seat.
  const { hasAccess: hasCultivarSeat } = useModuleLicense('cultivar');

  // Blocks naming this organisation as the nursery that supplied their trees
  // and still waiting on an answer. Only a nursery ever has any, so the entry
  // point appears only when there is something behind it — a permanent button
  // reading "0" on every other org's screen is just noise.
  const [openSupplyClaims, setOpenSupplyClaims] = useState(0);

  // Cultivars this organisation has been admitted to propagate. Only a
  // propagator has any, so the offers entry point appears only where there
  // is something to offer.
  const [admittedCultivars, setAdmittedCultivars] = useState(0);

  const [cultivars, setCultivars] = useState<Cultivar[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Parse filters from URL params
  const getFiltersFromParams = (): CultivarFilterValues => ({
    search: searchParams.get('search') || '',
    crop_type_id: searchParams.get('crop_type_id') ? Number(searchParams.get('crop_type_id')) : undefined,
    cultivar_role: searchParams.get('cultivar_role') === 'rootstock' ? 'rootstock' : searchParams.get('cultivar_role') === 'scion' ? 'scion' : '',
    status: searchParams.get('status') || '',
    protection_status: searchParams.get('protection_status') || '',
  });

  useEffect(() => {
    if (!hasCultivarSeat) return;
    let cancelled = false;
    cultivarService
      .listSupplyClaims({ status: 'unanswered' })
      .then(res => {
        if (!cancelled && res.success && res.data) setOpenSupplyClaims(res.data.length);
      })
      .catch(() => { /* an entry point that fails to appear is not an error */ });
    cultivarService
      .listMyPropagatorTerms()
      .then(res => {
        if (!cancelled && res.success && res.data) setAdmittedCultivars(res.data.length);
      })
      .catch(() => { /* same */ });
    return () => { cancelled = true; };
  }, [hasCultivarSeat]);

  const [filters, setFilters] = useState<CultivarFilterValues>(getFiltersFromParams());
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);

  const loadCultivars = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await cultivarService.searchCultivars({
        search: filters.search || undefined,
        crop_type_id: filters.crop_type_id,
        cultivar_role: filters.cultivar_role || undefined,
        status: filters.status || undefined,
        protection_status: filters.protection_status || undefined,
        page: currentPage,
        limit: 12,
      });

      if (response.success && response.data) {
        setCultivars(response.data.cultivars);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to load cultivars');
      }
    } catch (err) {
      console.error('Error loading cultivars:', err);
      setError('Failed to load cultivars. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage]);

  useEffect(() => {
    loadCultivars();
  }, [loadCultivars]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.crop_type_id) params.set('crop_type_id', String(filters.crop_type_id));
    if (filters.cultivar_role) params.set('cultivar_role', filters.cultivar_role);
    if (filters.status) params.set('status', filters.status);
    if (filters.protection_status) params.set('protection_status', filters.protection_status);
    if (currentPage > 1) params.set('page', String(currentPage));
    setSearchParams(params, { replace: true });
  }, [filters, currentPage, setSearchParams]);

  const handleFilterChange = (newFilters: CultivarFilterValues) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Per-org editing: a pbr or admin licence holder can edit cultivars created
  // by their own organisation. Cross-org editing no longer exists.
  const canEditCultivar = (cultivar: Cultivar) =>
    canWrite && user?.organisation_id === cultivar.created_by_org_id;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Cultivar Registry"
        subtitle="Browse and manage registered cultivars, varieties, and their intellectual property"
        icon={<Leaf className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap items-center gap-2">
            {/* Any cultivar seat: evaluators walking a trial block meet trees
                nobody has registered yet. */}
            {hasCultivarSeat && (
              <Button
                variant="outline"
                onClick={() => navigate('/cultivar/quick-evaluations')}
              >
                <ClipboardPen className="h-4 w-4" />
                Quick evaluations
              </Button>
            )}
            {hasCultivarSeat && (
              <Button
                variant="outline"
                onClick={() => navigate('/cultivar/maturity-chart')}
              >
                <BarChart3 className="h-4 w-4" />
                Maturity chart
              </Button>
            )}
            {admittedCultivars > 0 && (
              <Button
                variant="outline"
                onClick={() => navigate('/cultivar/nursery-offers')}
              >
                <Sprout className="h-4 w-4" />
                Your offers
              </Button>
            )}
            {openSupplyClaims > 0 && (
              <Button
                variant="outline"
                onClick={() => navigate('/cultivar/supply-claims')}
              >
                <ClipboardCheck className="h-4 w-4" />
                Supply claims
                <span className="ml-1.5 text-xs font-semibold bg-amber-100 text-amber-900 rounded-full px-1.5 py-0.5">
                  {openSupplyClaims}
                </span>
              </Button>
            )}
            {/* TEMPORARY: the legacy import, off 2026-10-28. */}
            {isCultivarAdmin && isLegacyCultivarImportOpen() && (
              <Button
                variant="outline"
                onClick={() => navigate('/cultivar/legacy-import')}
              >
                <History className="h-4 w-4" />
                Import from legacy
              </Button>
            )}
            {isCultivarAdmin && (
              <Button
                variant="outline"
                onClick={() => navigate('/cultivar/admin')}
              >
                <Settings className="h-4 w-4" />
                Admin
              </Button>
            )}
            {/* Registering needs the same pbr/admin seat as editing; a viewer
                would only fill in the form to have the save refused. */}
            {canWrite && (
              <Button
                variant="primary"
                onClick={() => navigate('/cultivar/create')}
              >
                <Plus className="h-4 w-4" />
                Register Cultivar
              </Button>
            )}
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="w-full lg:w-72 flex-shrink-0">
            <CultivarFilters
              onFilterChange={handleFilterChange}
              initialFilters={filters}
            />

            {/* Quick Stats */}
            {pagination && !loading && (
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-neutral-200 p-4">
                <h3 className="text-sm font-semibold text-neutral-700 mb-3">Results Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Total Cultivars</span>
                    <span className="font-medium text-neutral-900">{pagination.total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Showing</span>
                    <span className="font-medium text-neutral-900">
                      {cultivars.length} of {pagination.total}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="h-10 w-10 text-primary-600 animate-spin mx-auto mb-4" />
                  <p className="text-neutral-600">Loading cultivars...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-error-50 border border-error-200 rounded-xl p-6 text-center">
                <p className="text-error-700 mb-4">{error}</p>
                <Button variant="outline" onClick={loadCultivars}>
                  Try Again
                </Button>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && cultivars.length === 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-12 text-center">
                <div className="h-16 w-16 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                  <Leaf className="h-8 w-8 text-neutral-400" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                  No Cultivars Found
                </h3>
                <p className="text-neutral-600 mb-6 max-w-md mx-auto">
                  {filters.search || filters.crop_type_id || filters.status || filters.protection_status
                    || filters.cultivar_role
                    ? 'No cultivars match your current filters. Try adjusting your search criteria.'
                    : canWrite
                      ? 'Be the first to register a cultivar in the system.'
                      : 'No cultivars have been registered yet.'}
                </p>
                {canWrite && (
                  <Button variant="primary" onClick={() => navigate('/cultivar/create')}>
                    <Plus className="h-4 w-4" />
                    Register Cultivar
                  </Button>
                )}
              </div>
            )}

            {/* Cultivar Grid */}
            {!loading && !error && cultivars.length > 0 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 auto-rows-fr">
                  {cultivars.map((cultivar) => (
                    <CultivarCard
                      key={cultivar.id}
                      cultivar={cultivar}
                      canEdit={canEditCultivar(cultivar)}
                    />
                  ))}
                </div>

                {cultivars.some((c) => isPbrProtected(c.protection_status)) && (
                  <div className="mt-6">
                    <PbrNotice />
                  </div>
                )}

                {/* Pagination */}
                {pagination && pagination.pages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                        .filter(page => {
                          // Show first, last, current, and adjacent pages
                          return page === 1 ||
                            page === pagination.pages ||
                            Math.abs(page - currentPage) <= 1;
                        })
                        .map((page, index, array) => {
                          // Add ellipsis if there's a gap
                          const prevPage = array[index - 1];
                          const showEllipsis = prevPage && page - prevPage > 1;

                          return (
                            <div key={page} className="flex items-center gap-1">
                              {showEllipsis && (
                                <span className="px-2 text-neutral-400">...</span>
                              )}
                              <Button
                                variant={page === currentPage ? 'primary' : 'ghost'}
                                size="sm"
                                onClick={() => handlePageChange(page)}
                                className="min-w-[40px]"
                              >
                                {page}
                              </Button>
                            </div>
                          );
                        })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === pagination.pages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CultivarRegistry;
